import { useContext } from "react";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { SettingRow } from "./SettingRow";
import { MainNavScreens, LeftNavStyle } from "breadcommon";
import {
  setWebHideDollarAmountsPreference,
  setWebHomeScreenPreference,
  setWebLeftNavStylePreference,
  setWebDefaultCurrencyPreference,
} from "../firebaseio/preferencesIo";
import { UserContext } from "../firebaseio/UserContext";
import { RyeToggle } from "../rye/RyeToggle";
import { RyeDropDown } from "../rye/RyeDropDown";
import { RyeIcon } from "../rye/RyeIcon";
import { SUPPORTED_CURRENCIES } from "../firebaseio/ExchangeRatesContext";
import { getCurrencySymbol } from "../common/DollarAmount";
import { buildClasses } from "../utils/buildClasses";
import {
  MAIN_NAV_DEFINITIONS,
  MAIN_NAV_ORDER,
} from "../main_nav/mainNavDefinitions";

export function GeneralSettings(): JSX.Element {
  const user = useContext(UserContext);
  const userPrefs = useContext(PreferencesContext);

  return (
    <div>
      <SettingRow
        label="Home Screen"
        description="This is the page that will be opened when you open Bread"
        picker={
          <RyeDropDown<MainNavScreens>
            entries={MAIN_NAV_ORDER.map((screen) => ({
              id: screen,
              text: MAIN_NAV_DEFINITIONS[screen].label,
              icon: (
                <RyeIcon size="sm" name={MAIN_NAV_DEFINITIONS[screen].icon} />
              ),
            }))}
            initEntryId={userPrefs.web.home_screen}
            onSelectEntry={async (entry) =>
              await setWebHomeScreenPreference(user.uid, entry.id)
            }
            width="full"
          />
        }
      />
      <SettingRow
        label="Navigation Style"
        description="Controls how the left navigation bar behaves"
        picker={
          <RyeDropDown<LeftNavStyle>
            entries={[
              {
                id: LeftNavStyle.ALWAYS_COLLAPSED,
                text: "Always Collapsed",
                icon: <RyeIcon size="sm" name="dock_to_right" />,
              },
              {
                id: LeftNavStyle.EXPAND_ON_HOVER,
                text: "Expand on Hover",
                icon: <RyeIcon size="sm" name="left_panel_open" />,
              },
              {
                id: LeftNavStyle.ALWAYS_EXPANDED,
                text: "Always Expanded",
                icon: <RyeIcon size="sm" name="left_panel_close" />,
              },
            ]}
            initEntryId={userPrefs.web.left_nav_style}
            onSelectEntry={async (entry) =>
              await setWebLeftNavStylePreference(user.uid, entry.id)
            }
            width="full"
          />
        }
      />
      <SettingRow
        label="Default Currency"
        description="The primary currency used to aggregate and display budgets, reports, and total balances"
        picker={
          <RyeDropDown<string>
            entries={SUPPORTED_CURRENCIES.map((curr) => ({
              id: curr,
              text: curr,
              icon: (
                <span
                  className={buildClasses(
                    "font-mono",
                    "text-sm",
                    "font-semibold",
                    "w-5",
                    "text-center",
                    "text-on-surface-600",
                    "select-none",
                  )}
                >
                  {getCurrencySymbol("en-US", curr)}
                </span>
              ),
            }))}
            initEntryId={userPrefs.web.default_currency ?? "USD"}
            onSelectEntry={async (entry) =>
              await setWebDefaultCurrencyPreference(user.uid, entry.id)
            }
            searchable={true}
            width="full"
          />
        }
      />
      <SettingRow
        label="Hide Dollar Amounts"
        description='All dollar amounts on the site will be shown simply as "##"'
        picker={
          <RyeToggle
            initIsChecked={userPrefs.web.hide_dollar_amounts}
            onChange={async (newState: boolean) =>
              await setWebHideDollarAmountsPreference(user.uid, newState)
            }
          />
        }
      />
    </div>
  );
}
